import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Chip } from 'primeng/chip';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import {
  cleanupOverlaysOnNavigation,
  removeOrphanedOverlays,
} from '../../../utilities/overlay-cleanup';
import { SearchFilterStatusComponent } from '../labels/search-filter-status.component';
import { BaseControlComponent } from './base-control.component';
import { FormFieldComponent } from './form-field.component';

/**
 * WORKAROUND: PrimeNG clear icon a11y (WCAG)
 * Data attribute used to mark clear icons we make keyboard-accessible
 */
const A11Y_CLEAR_ATTR = 'data-a11y-clear-button';

/**
 * WORKAROUND: PrimeNG clear icon a11y (WCAG)
 * Selector for PrimeNG clear icon elements (p-select, p-multiselect)
 */
const A11Y_CLEAR_ICON_SELECTOR =
  '.p-select-clear-icon, .p-autocomplete-clear-icon, [data-pc-section="clearicon"]';

/**
 * WORKAROUND: PrimeNG MultiSelect "Select all" checkbox should toggle via Enter as well as Space.
 * Native checkbox behavior is Space-only; this enables consistent keyboard activation.
 */
const MULTISELECT_TOGGLE_ALL_INPUT_SELECTOR =
  '.p-multiselect-header .p-checkbox-input';

@Component({
  selector: 'app-select',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    Select,
    MultiSelectModule,
    Chip,
    FormFieldComponent,
    SearchFilterStatusComponent,
  ],
  template: `
    <app-form-field
      [control]="control"
      [id]="id"
      [errorId]="errorId"
      [label]="loading ? null : label"
      [helpText]="helpText"
      [showHelpText]="showHelpText"
      [classes]="classes"
      [placeholder]="loading ? placeholder || 'Loading...' : placeholder"
      [screenReaderLabel]="screenReaderLabel"
      [useLabelFor]="useLabelFor ?? multiple"
    >
      <app-search-filter-status
        announceMode="filter"
        [count]="filterResultCount"
        [query]="filterQuery"
        statusStyle="available"
      />
      @if (multiple) {
        <p-multiselect
          [inputId]="id"
          [ariaLabel]="
            (label || screenReaderLabel || placeholder || 'Select options') +
            (options?.length
              ? ' dropdown, has ' + options.length + ' options'
              : ' dropdown')
          "
          [ariaLabelledBy]="
            ariaLabelledBy ||
            (label || screenReaderLabel || placeholder ? id + '-label' : null)
          "
          [required]="required"
          [options]="options"
          [formControl]="control"
          [optionLabel]="optionLabel"
          [optionGroupLabel]="optionLabel"
          [optionValue]="optionValue"
          [optionDisabled]="optionDisabled"
          [filter]="multipleFilter"
          [filterBy]="filterBy || optionLabel"
          [showClear]="showClear"
          [loading]="loading"
          [attr.aria-busy]="loading ? true : null"
          [group]="group"
          class="tw-w-full"
          styleClass="tw-font-bold tw-w-full"
          [appendTo]="'body'"
          [virtualScroll]="virtualScroll"
          [virtualScrollOptions]="{
            itemSize: virtualScrollItemSize,
            scrollHeight: '200px',
          }"
          (onClear)="clear.emit($event)"
          (onShow)="show.emit(this)"
          (onClick)="onClick()"
          (onPanelShow)="handlePanelShow($event)"
          (onPanelHide)="handlePanelHide($event)"
          (onFocus)="onFocus()"
          (onFilter)="handleMultiSelectFilter($event)"
          [attr.title]="label"
          [class]="{
            'ng-invalid': touchedInvalid,
            'ng-dirty': touchedInvalid,
          }"
          [placeholder]="loading ? placeholder || 'Loading...' : placeholder"
          [display]="chip ? 'chip' : 'comma'"
          [maxSelectedLabels]="
            lockedOptions.length > 0
              ? 0
              : chip
                ? maxSelectedLabelsChip
                : maxSelectedLabels
          "
          [showHeader]="showHeader"
          [selectedItemsLabel]="selectedItemsLabel"
          [showToggleAll]="showToggleAll"
        >
          <ng-template pTemplate="selectedItems" let-value>
            @if (chip) {
              @if (
                lockedOptions.length > 0 ||
                (value?.length <= maxSelectedLabelsChip &&
                  value?.length < options?.length)
              ) {
                <div class="custom-chips-container">
                  @for (item of value; track item[optionValue] ?? $index) {
                    <p-chip
                      [label]="item[optionLabel]"
                      [removable]="!isOptionLocked(item)"
                      (onRemove)="removeChip(item)"
                    />
                  }
                  @if (!value || value.length === 0) {
                    <span class="p-placeholder">{{ placeholder }}</span>
                  }
                </div>
              } @else {
                <span>{{ getSelectedItemsLabel(value?.length || 0) }}</span>
              }
            } @else {
              <span>{{ getSelectedItemsLabel(value?.length || 0) }}</span>
            }
          </ng-template>
          <ng-template #selectedItem let-item>
            <span class="option-label"> {{ item[optionLabel] }} </span>
          </ng-template>
          <ng-template let-item #item>
            @if (dropdownOptionTemplate) {
              <ng-container
                *ngTemplateOutlet="
                  dropdownOptionTemplate;
                  context: { item: item }
                "
              ></ng-container>
            } @else if (optionTemplate) {
              <ng-container
                *ngTemplateOutlet="optionTemplate; context: { item: item }"
              ></ng-container>
            } @else {
              <span class="option-label" [attr.aria-label]="item[optionLabel]">
                {{ item[optionLabel] }}
              </span>
            }
          </ng-template>
          <ng-template pTemplate="header">
            @if (multipleFilter) {
              <div class="tw-sr-only">
                {{ selectAllLabel }}
              </div>
            } @else if (showHeader) {
              <div class="checkbox-all-text option-label">
                {{ selectAllLabel }}
              </div>
            }
          </ng-template>
        </p-multiselect>
      } @else {
        <p-select
          [inputId]="id"
          [ariaLabel]="
            (label || screenReaderLabel || placeholder || 'Select option') +
            (options?.length
              ? ' dropdown, has ' + options.length + ' options'
              : ' dropdown')
          "
          [ariaLabelledBy]="
            ariaLabelledBy ||
            (label || screenReaderLabel || placeholder ? id + '-label' : null)
          "
          [required]="required"
          [options]="options"
          [formControl]="control"
          [optionLabel]="optionLabel"
          [optionGroupLabel]="optionLabel"
          [optionValue]="optionValue"
          [filter]="filter"
          [filterBy]="filterBy || optionLabel"
          [showClear]="showClear"
          [loading]="loading"
          [attr.aria-busy]="loading ? true : null"
          [group]="group"
          class="tw-w-full"
          styleClass="tw-w-full"
          [appendTo]="'body'"
          [virtualScroll]="virtualScroll"
          [virtualScrollItemSize]="virtualScrollItemSize"
          (onShow)="onSelectShow($event)"
          (onHide)="handlePanelHide($event)"
          (onClick)="onClick()"
          (onFilter)="handleSelectFilter($event)"
          (onFocus)="onFocus()"
          [attr.title]="label"
          [class]="{
            'ng-invalid': touchedInvalid,
            'ng-dirty': touchedInvalid,
          }"
          [placeholder]="loading ? placeholder || 'Loading...' : placeholder"
        >
          <ng-template #selectedItem let-item>
            <div
              class="option-label tw-w-full"
              [attr.aria-label]="item[optionLabel]"
            >
              @if (optionTemplate && !dropdownOptionTemplate) {
                <ng-container
                  *ngTemplateOutlet="optionTemplate; context: { item: item }"
                ></ng-container>
              } @else {
                {{ item[optionLabel] }}
              }
            </div>
          </ng-template>

          <ng-template let-item #item>
            <div
              class="option-label tw-w-full"
              [attr.aria-label]="item[optionLabel]"
            >
              @if (dropdownOptionTemplate) {
                <ng-container
                  *ngTemplateOutlet="
                    dropdownOptionTemplate;
                    context: { item: item }
                  "
                ></ng-container>
              } @else if (optionTemplate) {
                <ng-container
                  *ngTemplateOutlet="optionTemplate; context: { item: item }"
                ></ng-container>
              } @else {
                {{ item[optionLabel] }}
              }
            </div>
          </ng-template>
        </p-select>
      }
    </app-form-field>
  `,
  styles: `
    .checkbox-all-text {
      color: var(--sed-engaged-black-color);
      position: absolute;
      left: 38px;
      top: 6px;
      height: 68px;
    }
    .option-label {
      color: var(--sed-black-80-color);
      font-weight: bold;
    }
    .disabled {
      background-color: var(--sed-grey-40-color);
      color: var(--sed-black-80-color);
      border: 1px solid var(--sed-black-60-color);
    }
    .custom-chips-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      align-items: center;
      min-height: 1.75rem;
    }
    :host ::ng-deep .custom-chips-container .p-chip {
      max-width: 100%;
    }
    :host ::ng-deep .custom-chips-container .p-chip-label {
      white-space: normal;
      word-break: break-word;
    }
    :host ::ng-deep p-floatlabel:has(.p-select, .p-multiselect) label {
      /* Keep float label from covering clear/dropdown icons */
      max-width: calc(100% - 2.25rem - 0.75rem);
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    :host
      ::ng-deep
      p-floatlabel:has(
        .p-select.p-select-clearable,
        .p-multiselect:has(
          .p-multiselect-clear-icon,
          [data-pc-section='clearicon']
        )
      )
      label {
      max-width: calc(100% - 3.75rem - 0.75rem);
    }
    :host ::ng-deep .p-multiselect .p-multiselect-label {
      /* Keep selected/placeholder text from running under the dropdown arrow */
      padding-right: 2.25rem;
    }
    :host
      ::ng-deep
      .p-multiselect:has(
        .p-multiselect-clear-icon,
        [data-pc-section='clearicon']
      )
      .p-multiselect-label {
      /* Reserve extra space when clear and dropdown icons are both visible */
      padding-right: 3.75rem;
    }
    :host ::ng-deep .p-select .p-select-label {
      /* Keep selected text from running under the dropdown arrow */
      padding-right: 2.25rem;
    }
    :host ::ng-deep .p-select.p-select-clearable .p-select-label {
      /* Reserve extra space when clear and dropdown icons are both visible */
      padding-right: 3.75rem;
    }
  `,
})
export class SelectComponent
  extends BaseControlComponent
  implements AfterViewInit, AfterViewChecked, OnDestroy
{
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly router = inject(Router);

  /**
   * Overlay selectors this component owns. Used to sweep orphaned panels
   * from <body> when the host is destroyed or the route changes while a
   * panel is open (PrimeNG's hide animation can race with destruction).
   */
  private static readonly OVERLAY_SELECTORS = [
    '.p-select-overlay',
    '.p-multiselect-overlay',
  ];

  /**
   * Binds `<label for>` via FormField. Single `p-select` uses a non-labelable combobox span;
   * omit this input to use `multiple` as the default (single → false, multiselect → true).
   * Set explicitly to override.
   */
  @Input()
  useLabelFor?: boolean;

  // Timeout constants
  private readonly TIMEOUT_DELAY = 100;
  private readonly MAX_RETRY_COUNT = 5;

  @Input({ required: true })
  optionLabel: string;

  @Input()
  optionValue: string;

  @Input({ required: true })
  options: any[];

  @Input()
  multiple = false;

  @Input()
  chip = false;

  @Input()
  maxSelectedLabels = 3;

  @Input()
  maxSelectedLabelsChip = 10;

  @Input()
  multipleFilter = false;

  @Input()
  virtualScroll = true;

  @Input()
  virtualScrollItemSize = 38;

  @Input()
  filter = true;

  @Input()
  group = false;

  @Input()
  showClear = true;

  @Input()
  loading = false;

  @Input()
  showHeader = true;

  @Input()
  optionDisabled: string;

  @Input()
  selectAllLabel = 'Select all';

  @Input()
  selectedItemsLabel: string;

  @Input()
  classes: string;

  /** Screen reader only label - used when no visible label but accessibility name is needed */
  @Input()
  screenReaderLabel: string;

  /** ID of the element that labels this select (WCAG: use when label is rendered outside the control). */
  @Input()
  ariaLabelledBy: string;

  @Input()
  showToggleAll = true;

  @Input()
  lockedOptions: any[] = [];

  @Input()
  optionTemplate: TemplateRef<any>;

  /** Dropdown list only; selected value still uses {@link optionLabel}. */
  @Input()
  dropdownOptionTemplate: TemplateRef<any>;

  /** PrimeNG filter fields; defaults to {@link optionLabel}. */
  @Input()
  filterBy: string;

  @Output()
  clear = new EventEmitter<any>();

  @Output()
  show = new EventEmitter<SelectComponent>();

  /**
   * WORKAROUND: PrimeNG #19442 - virtual scroll + filter causes options to stay hidden
   */
  @ViewChild(Select)
  private pSelect: Select;

  @ViewChild(MultiSelect)
  private pMultiSelect: MultiSelect;

  private panelTabTrapHandler: ((e: KeyboardEvent) => void) | null = null;

  private lastFocusedElement: HTMLElement | null = null;
  private observer: MutationObserver | null = null;
  /** WORKAROUND: PrimeNG clear icon a11y. REMOVE WHEN: PrimeNG fixes clear icon (#17404). */
  private hostKeydownListener: ((e: KeyboardEvent) => void) | null = null;

  private selectKeydownFallbackListener: ((e: KeyboardEvent) => void) | null =
    null;

  /**
   * Stable listener ref for overlay filter inputs (add/removeEventListener must use the same fn).
   * Handles Escape to restore focus and Space to match Enter when an option is highlighted.
   */
  private readonly boundFilterInputKeydown = (e: KeyboardEvent) =>
    this.handleFilterInputKeydown(e);

  private readonly boundFilterInputInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    if (
      target.classList.contains('p-select-filter') ||
      target.classList.contains('p-multiselect-filter')
    ) {
      this.updateFilterAnnouncement(target.value ?? '');
    }
  };

  /**
   * Tracks the currently visible overlay panel element (both p-select and p-multiselect).
   * Used by the mousedown listener to dismiss the overlay on outside clicks.
   */
  private currentOverlayPanel: HTMLElement | null = null;
  private boundMousedownHandler = this.onDocumentMousedown.bind(this);

  protected filterResultCount = 0;

  protected filterQuery = '';

  isOptionLocked(item: any): boolean {
    const value = this.optionValue ? item[this.optionValue] : item;
    return this.lockedOptions.includes(value);
  }

  removeChip(item: any): void {
    const value = this.optionValue ? item[this.optionValue] : item;
    const currentValues = this.control.value || [];
    const newValues = currentValues.filter((v: any) => v !== value);
    this.control.setValue(newValues);
  }

  getSelectedItemsLabel(count: number): string {
    const totalOptions = this.group
      ? this.options?.reduce((sum, g) => sum + (g.items?.length || 0), 0)
      : this.options?.length;

    if (count === totalOptions) {
      return 'All';
    }
    return (this.selectedItemsLabel || '{0} item{1} selected')
      .replace('{0}', count.toString())
      .replace('{1}', count === 1 ? '' : 's');
  }

  /**
   * WORKAROUND: PrimeNG Select (WCAG)
   * aria-describedby must be set on the inner role="combobox" node; PrimeNG does not support it.
   * ngAfterViewChecked: error visibility (touched/dirty) and PrimeNG re-renders.
   * statusChanges + queueMicrotask: valid ↔ invalid transitions after view updates.
   */
  private syncAriaDescribedBy(): void {
    const combobox = this.elementRef.nativeElement.querySelector(
      '[role="combobox"]',
    ) as HTMLElement | null;
    if (!combobox) return;
    const ids: string[] = [];

    if (this.ariaDescribedBy) ids.push(this.ariaDescribedBy);
    if (this.describedBy) ids.push(this.describedBy);
    const next = ids.length ? ids.join(' ') : null;
    const current = combobox.getAttribute('aria-describedby');

    if (current !== next) {
      if (next) combobox.setAttribute('aria-describedby', next);
      else combobox.removeAttribute('aria-describedby');
    }
  }

  /**
   * WORKAROUND: PrimeNG #19442. REMOVE WHEN: PrimeNG v21.1.2+ (PR #19451)
   * Calls scroller internals directly instead of scroller.init() because init()
   * defers its work behind an internal setTimeout(..., 1), causing a timing race
   * that leaves the viewport stale when the user corrects filter input rapidly.
   */
  handleSelectFilter(event?: { filter: string }): void {
    if (this.virtualScroll && this.pSelect?.scroller) {
      const scroller = this.pSelect.scroller;
      setTimeout(() => {
        scroller.setSpacerSize();
        scroller.setSize();
        scroller.calculateOptions();
        scroller.calculateAutoSize();
        scroller.scrollToIndex(0);
        scroller.cd.detectChanges();
      });
    }

    this.updateFilterAnnouncement(event?.filter ?? '');
  }

  handleMultiSelectFilter(event?: { filter: string }): void {
    this.updateFilterAnnouncement(event?.filter ?? '');
  }

  ngAfterViewInit(): void {
    this.setupOverlayObserver();
    /* WORKAROUND: PrimeNG clear icon a11y. REMOVE WHEN: PrimeNG fixes clear icon (#17404). */
    this.setupClearIconAccessibility();
    this.setupSelectKeyboardFallback();
    this.makeClearIconsAccessible();
    this.makeChipRemoveIconsAccessible();
    this.applyComboboxAriaAttributes();
    this.dispose.timeouts = setTimeout(() => {
      this.makeClearIconsAccessible();
      this.makeChipRemoveIconsAccessible();
      this.applyComboboxAriaAttributes();
    }, this.TIMEOUT_DELAY * 2);
    this.dispose.subs = this.control.valueChanges.subscribe(() => {
      this.dispose.timeouts = setTimeout(() => {
        this.makeClearIconsAccessible();
        this.makeChipRemoveIconsAccessible();
        this.applyComboboxAriaAttributes();
      }, this.TIMEOUT_DELAY);
    });

    this.dispose.subs = this.control.statusChanges.subscribe(() =>
      queueMicrotask(() => this.syncAriaDescribedBy()),
    );

    /*
     * WORKAROUND: PrimeNG overlay panels appended to <body> can be left
     * behind in the DOM when the host is destroyed mid-animation (e.g.
     * router navigation while a dropdown is open on a mobile device).
     * Hide normally on NavigationStart and sweep any stragglers.
     */
    this.dispose.subs = cleanupOverlaysOnNavigation(this.router, () =>
      this.cleanupOverlays(),
    );
  }

  ngAfterViewChecked(): void {
    this.syncAriaDescribedBy();
  }

  override ngOnDestroy(): void {
    this.observer?.disconnect();
    this.unbindDocumentMousedown();
    /* WORKAROUND: PrimeNG clear icon a11y. REMOVE WHEN: PrimeNG fixes clear icon (#17404). */
    if (this.hostKeydownListener) {
      document.removeEventListener('keydown', this.hostKeydownListener, true);
    }
    if (this.selectKeydownFallbackListener) {
      this.elementRef.nativeElement.removeEventListener(
        'keydown',
        this.selectKeydownFallbackListener,
        true,
      );
      this.selectKeydownFallbackListener = null;
    }
    this.cleanupOverlays();
    super.ngOnDestroy();
  }

  private updateFilterAnnouncement(filterText: string): void {
    this.filterQuery = filterText;
    this.filterResultCount = this.getSearchFilterOptionCount(filterText);
  }

  /**
   * Counts options matching `query` using the same "contains" semantics as
   * PrimeNG's FilterService (see primeng-api.mjs `filters.contains`), which
   * only trims to detect an empty filter — it does not trim the filter text
   * itself before matching. Trimming here caused announced counts to
   * disagree with the actually-filtered list whenever the query had
   * meaningful whitespace (e.g. "al " matched fewer options than "al").
   */
  private getSearchFilterOptionCount(query: string): number {
    const flatOptions = this.group
      ? this.options.flatMap((g: any) => g.items ?? [])
      : (this.options ?? []);

    if (!query || query.trim() === '') {
      return flatOptions.length;
    }

    const normalizedQuery = query.toLowerCase();

    const fields = (this.filterBy || this.optionLabel)
      .split(',')
      .map((f) => f.trim());

    return flatOptions.filter((option) =>
      fields.some((field) =>
        String(option[field] ?? '')
          .toLowerCase()
          .includes(normalizedQuery),
      ),
    ).length;
  }

  /**
   * Hide PrimeNG overlays this component owns and remove any DOM remnants.
   * Safe to call multiple times.
   */
  private cleanupOverlays(): void {
    this.pSelect?.hide();
    this.pMultiSelect?.hide();
    this.currentOverlayPanel = null;
    removeOrphanedOverlays(SelectComponent.OVERLAY_SELECTORS);
  }

  private onDocumentMousedown(event: MouseEvent): void {
    const target = event.target as Node;
    if (!target) return;
    if (this.elementRef.nativeElement.contains(target)) return;
    if (this.currentOverlayPanel?.contains(target)) return;
    this.pSelect?.hide();
    this.pMultiSelect?.hide();
    this.currentOverlayPanel = null;
  }

  private bindDocumentMousedown(): void {
    document.addEventListener('mousedown', this.boundMousedownHandler);
  }

  private unbindDocumentMousedown(): void {
    document.removeEventListener('mousedown', this.boundMousedownHandler);
  }

  /**
   * WORKAROUND: PrimeNG clear icon not keyboard-accessible (WCAG)
   * Adds document keydown listener so Enter/Space activates the clear icon
   * REMOVE WHEN: PrimeNG fixes clear icon
   * See: https://github.com/primefaces/primeng/issues/17404
   */
  private setupClearIconAccessibility(): void {
    if (this.hostKeydownListener) return;
    this.hostKeydownListener = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const clearBtn = target.closest?.(`[${A11Y_CLEAR_ATTR}]`);
      if (!clearBtn || (e.key !== 'Enter' && e.key !== ' ')) return;
      if (!this.elementRef.nativeElement.contains(clearBtn)) return;
      e.preventDefault();
      (clearBtn as Element).dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        }),
      );
    };
    document.addEventListener('keydown', this.hostKeydownListener, true);
  }

  /**
   * WORKAROUND: PrimeNG clear icon not keyboard-accessible (WCAG)
   * Patches clear icon elements with tabindex, role, aria-label so they are focusable and announced as buttons by screen readers
   */
  private makeClearIconsAccessible(): void {
    if (!this.showClear) return;
    const host = this.elementRef.nativeElement;
    const fromHost = host.querySelectorAll<HTMLElement>(
      A11Y_CLEAR_ICON_SELECTOR,
    );
    const clearIcons: HTMLElement[] =
      fromHost.length > 0
        ? Array.from(fromHost)
        : Array.from(
            document.querySelectorAll<HTMLElement>(A11Y_CLEAR_ICON_SELECTOR),
          ).filter((el) => host.contains(el));
    clearIcons.forEach((icon) => {
      if (icon.hasAttribute(A11Y_CLEAR_ATTR)) return;
      icon.setAttribute('tabindex', '0');
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-label', 'Clear selection');
      icon.setAttribute(A11Y_CLEAR_ATTR, 'true');
    });
  }

  /**
   * WORKAROUND: PrimeNG Chip remove icon uses generic "Remove" aria-label (WCAG).
   * Patch each chip remove control with the chip label for context.
   */
  private makeChipRemoveIconsAccessible(): void {
    if (!this.chip || !this.multiple) return;
    const host = this.elementRef.nativeElement;
    host
      .querySelectorAll<HTMLElement>('.custom-chips-container .p-chip')
      .forEach((chip) => {
        const label = chip.querySelector('.p-chip-label')?.textContent?.trim();
        if (!label) return;
        const removeIcon = chip.querySelector<HTMLElement>(
          '.p-chip-remove-icon[role="button"]',
        );
        if (removeIcon) {
          removeIcon.setAttribute('aria-label', `Remove ${label}`);
        }
      });
  }

  /**
   * WORKAROUND: PrimeNG does not reliably set aria-required, aria-autocomplete,
   * or human-readable value on the internal combobox element. Patch them post-render.
   */
  private applyComboboxAriaAttributes(): void {
    const host = this.elementRef.nativeElement;
    const combobox = host.querySelector('[role="combobox"]') as HTMLElement;
    if (!combobox) return;

    if (this.required) {
      combobox.setAttribute('aria-required', 'true');
    } else {
      combobox.removeAttribute('aria-required');
    }

    const autocompleteValue = (
      this.multiple ? this.multipleFilter : this.filter
    )
      ? 'list'
      : 'none';
    combobox.setAttribute('aria-autocomplete', autocompleteValue);

    if (this.multiple) {
      this.applyReadableMultiselectValue(combobox);
    }
  }

  /**
   * PrimeNG MultiSelect writes raw option values (e.g. "PR,AU,GL") or
   * "[object Object]" into the hidden combobox input's value attribute.
   * Screen readers announce these instead of human-readable labels.
   * Translate them post-render.
   */
  private applyReadableMultiselectValue(combobox: HTMLElement): void {
    const selectedValues = this.control.value as any[];
    if (!selectedValues?.length || !this.options?.length) return;

    let labels: string[];

    if (!this.optionValue) {
      labels = selectedValues
        .map((val) => val?.[this.optionLabel])
        .filter(Boolean);
    } else {
      const flatOptions = this.group
        ? this.options.flatMap((g: any) => g.items || [])
        : this.options;

      labels = selectedValues
        .map((val) => {
          const option = flatOptions.find(
            (o: any) => o[this.optionValue] === val,
          );
          return option ? option[this.optionLabel] : null;
        })
        .filter(Boolean);
    }

    if (labels.length) {
      combobox.setAttribute('value', labels.join(', '));
    }
  }

  private setupOverlayObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const hasOverlay = Array.from(mutation.addedNodes).some((node) => {
            if (node instanceof HTMLElement) {
              return (
                node.classList.contains('p-select-overlay') ||
                node.classList.contains('p-multiselect-overlay')
              );
            }
            return false;
          });
          if (hasOverlay) {
            this.dispose.timeouts = setTimeout(
              () => this.addAccessibleLabelToOverlay(),
              this.TIMEOUT_DELAY,
            );
          }
        }
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: false,
    });
  }

  private addAccessibleLabelToOverlay(): void {
    const overlays = document.querySelectorAll(
      '.p-select-overlay, .p-multiselect-overlay',
    );
    const overlayPanel = overlays[overlays.length - 1] as HTMLElement;

    if (overlayPanel) {
      this.addAccessibleLabelToSearchInput(overlayPanel);
    }
  }

  onSelectShow(event: any): void {
    this.show.emit(this);
    this.handlePanelShow(event);
  }

  handlePanelShow(event: any) {
    if (event.element) {
      this.currentOverlayPanel = event.element;
      this.bindDocumentMousedown();

      const inputs = (event.element as HTMLElement).querySelectorAll('input');
      inputs.forEach((input) => {
        input.removeEventListener('keydown', this.boundFilterInputKeydown);
        input.addEventListener('keydown', this.boundFilterInputKeydown);
        input.removeEventListener('input', this.boundFilterInputInput);
        input.addEventListener('input', this.boundFilterInputInput);
      });

      this.dispose.timeouts = setTimeout(
        () => this.addAccessibleLabelToSearchInput(event.element),
        this.TIMEOUT_DELAY,
      );

      const panel = event.element as HTMLElement;
      this.panelTabTrapHandler = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          const escapeEvent = new KeyboardEvent('keydown', {
            bubbles: true,
            cancelable: true,
            key: 'Escape',
            code: 'Escape',
          });
          this.lastFocusedElement?.dispatchEvent(escapeEvent);
          this.lastFocusedElement?.focus();
          return;
        }

        if (e.key === 'Enter') {
          const target = e.target as Element | null;
          if (!target) return;
          if (!target.matches(MULTISELECT_TOGGLE_ALL_INPUT_SELECTOR)) return;
          e.preventDefault();
          (target as HTMLInputElement).click();
          return;
        }

        this.forwardOverlayKeyboardToSelect(e);
      };
      panel.addEventListener('keydown', this.panelTabTrapHandler);
    }
  }

  handlePanelHide(event: any) {
    this.currentOverlayPanel = null;
    this.unbindDocumentMousedown();

    if (event.element) {
      const inputs = (event.element as HTMLElement).querySelectorAll('input');
      inputs.forEach((input) => {
        input.removeEventListener('keydown', this.boundFilterInputKeydown);
        input.removeEventListener('input', this.boundFilterInputInput);
      });

      const panel = event.element as HTMLElement;
      if (this.panelTabTrapHandler) {
        panel.removeEventListener('keydown', this.panelTabTrapHandler);
        this.panelTabTrapHandler = null;
      }
    }
  }

  onClick() {
    this.lastFocusedElement = document.activeElement as HTMLElement | null;
    this.dispose.timeouts = setTimeout(
      () => this.addAccessibleLabelToOverlay(),
      this.TIMEOUT_DELAY,
    );
  }

  open(): void {
    if (this.multiple) {
      this.pMultiSelect?.show();
    } else {
      this.pSelect?.show();
    }
  }

  close(): void {
    if (this.multiple) {
      this.pMultiSelect?.hide();
    } else {
      this.pSelect?.hide();
    }
  }

  onFocus() {
    this.lastFocusedElement = document.activeElement as HTMLElement | null;
    this.dispose.timeouts = setTimeout(
      () => this.addAccessibleLabelToOverlay(),
      this.TIMEOUT_DELAY,
    );
  }

  /**
   * PrimeNG keeps DOM focus on the filter input while keyboard highlight moves on options.
   * `onFilterKeyDown` treats Enter like “select highlighted option” but has no Space case,
   * so Space was inserted into the search field. Mirror Enter when an option is highlighted.
   */
  private handleFilterInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.lastFocusedElement?.focus();
      return;
    }

    if (
      event.key !== ' ' &&
      event.code !== 'Space' &&
      event.key !== 'Spacebar'
    ) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target || !(target instanceof HTMLInputElement)) {
      return;
    }
    if (target.type === 'checkbox') {
      return;
    }
    const isSelectFilter = target.classList.contains('p-select-filter');
    const isMultiFilter = target.classList.contains('p-multiselect-filter');
    if (!isSelectFilter && !isMultiFilter) {
      return;
    }

    if (this.multiple) {
      const ms = this.pMultiSelect as unknown as {
        focusedOptionIndex?: () => number;
        onEnterKey?: (e: KeyboardEvent) => void;
      };
      const idx = ms.focusedOptionIndex?.() ?? -1;
      if (idx >= 0 && ms.onEnterKey) {
        event.preventDefault();
        event.stopPropagation();
        ms.onEnterKey(event);
      }
      return;
    }

    const sel = this.pSelect as unknown as {
      focusedOptionIndex?: () => number;
      onEnterKey?: (e: KeyboardEvent, fromInput?: boolean) => void;
    };
    const idx = sel.focusedOptionIndex?.() ?? -1;
    if (idx >= 0 && sel.onEnterKey) {
      event.preventDefault();
      event.stopPropagation();
      sel.onEnterKey(event, true);
    }
  }

  private setupSelectKeyboardFallback(): void {
    if (this.selectKeydownFallbackListener) return;
    this.selectKeydownFallbackListener = (e: KeyboardEvent) => {
      if (!this.pSelect) return;
      if (e.defaultPrevented) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (!this.elementRef.nativeElement.contains(target)) return;

      const code = this.normalizeKeyboardCode(e);
      if (!code) return;
      if (!this.isSelectKeyboardFallbackCode(code)) return;

      // PrimeNG Select relies on KeyboardEvent.code; some assistive tech
      // (e.g., Windows Narrator) may emit missing/incorrect codes.
      const needsFallback = e.code !== code;
      if (!needsFallback) return;

      e.preventDefault();
      e.stopPropagation();
      this.pSelect.onKeyDown(this.createSyntheticKeydownEvent(e, code));
    };
    this.elementRef.nativeElement.addEventListener(
      'keydown',
      this.selectKeydownFallbackListener,
      true,
    );
  }

  private isSelectKeyboardFallbackCode(code: string): boolean {
    return (
      code === 'ArrowDown' ||
      code === 'ArrowUp' ||
      code === 'Enter' ||
      code === 'Space'
    );
  }

  private normalizeKeyboardCode(event: KeyboardEvent): string | null {
    const raw =
      event.code && event.code !== 'Unidentified' ? event.code : event.key;

    const normalized = {
      ArrowDown: 'ArrowDown',
      Down: 'ArrowDown',
      ArrowUp: 'ArrowUp',
      Up: 'ArrowUp',
      Home: 'Home',
      End: 'End',
      PageDown: 'PageDown',
      PageUp: 'PageUp',
      Enter: 'Enter',
      NumpadEnter: 'Enter',
      Return: 'Enter',
      ' ': 'Space',
      Space: 'Space',
      Spacebar: 'Space',
      Escape: 'Escape',
      Esc: 'Escape',
      Tab: 'Tab',
    } as const;

    return normalized[raw as keyof typeof normalized] ?? null;
  }

  private normalizeKeyboardKey(code: string): string {
    switch (code) {
      case 'Space':
        return ' ';
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case 'Escape':
      case 'Tab':
        return code;
      default:
        return code;
    }
  }

  private createSyntheticKeydownEvent(
    original: KeyboardEvent,
    code: string,
  ): KeyboardEvent {
    return new KeyboardEvent('keydown', {
      bubbles: true,
      cancelable: true,
      key: this.normalizeKeyboardKey(code),
      code,
      shiftKey: original.shiftKey,
      altKey: original.altKey,
      ctrlKey: original.ctrlKey,
      metaKey: original.metaKey,
      repeat: original.repeat,
    });
  }

  private forwardOverlayKeyboardToSelect(event: KeyboardEvent): void {
    if (!this.pSelect) return;
    if (event.defaultPrevented) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    // Overlay is appended to body; if focus is inside overlay (e.g., Narrator),
    // forward navigation keys back into PrimeNG Select keyboard handler.
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target.isContentEditable
    ) {
      return;
    }

    const code = this.normalizeKeyboardCode(event);
    if (!code) return;
    if (code === 'Tab') return;

    if (!this.isSelectKeyboardFallbackCode(code) && code !== 'Escape') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.pSelect.onKeyDown(this.createSyntheticKeydownEvent(event, code));
  }

  private addAccessibleLabelToSearchInput(
    panelElement: HTMLElement,
    retryCount = 0,
  ): void {
    const searchInput = panelElement.querySelector(
      '.p-select-filter, .p-multiselect-filter',
    ) as HTMLInputElement;

    if (!searchInput) {
      if (retryCount < this.MAX_RETRY_COUNT) {
        const delay = this.TIMEOUT_DELAY * (retryCount + 1);
        this.dispose.timeouts = setTimeout(
          () =>
            this.addAccessibleLabelToSearchInput(panelElement, retryCount + 1),
          delay,
        );
      }
      return;
    }

    const ariaLabelText = this.label
      ? `Search in ${this.label}`
      : 'Search options';
    searchInput.removeAttribute('role');
    searchInput.removeAttribute('aria-activedescendant');
    searchInput.removeAttribute('aria-labelledby');
    searchInput.setAttribute('aria-label', ariaLabelText);
    searchInput.setAttribute('inputmode', 'search');
    searchInput.setAttribute('spellcheck', 'false');
    // Keep PrimeNG [attr.aria-label]="ariaFilterLabel" populated across CD.
    if (this.pMultiSelect) {
      this.pMultiSelect.ariaFilterLabel = ariaLabelText;
    }

    const inputId = searchInput.id || `${this.id}-search-${Date.now()}`;
    const labelId = `${inputId}-label`;

    if (panelElement.querySelector(`#${labelId}`)) {
      return;
    }

    if (!searchInput.id) {
      searchInput.setAttribute('id', inputId);
    }

    const label = document.createElement('span');
    label.id = labelId;
    label.textContent = 'Search';
    label.className = 'search-input-label';

    searchInput.style.setProperty('padding-left', '0.5rem', 'important');

    const searchInputParent = searchInput.parentElement;
    if (searchInputParent) {
      searchInputParent.style.setProperty('position', 'relative', 'important');
      searchInputParent.style.setProperty('margin-top', '15px', 'important');
      searchInputParent.insertBefore(label, searchInput);
    }
  }
}
